import React from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleField, UserSelectField, LabelWithHelp, SecretPasswordField } from '../../../components';
import { Field } from 'formik';

interface GitlabFormProps {};

function GitlabForm(props: GitlabFormProps) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col lg:flex-row space-y-6 lg:space-y-0 space-x-0 lg:space-x-12">
            <div className="flex-1">
                <h2 className="mt-4 mb-1 text-base font-semibold leadin text-lg text-gray-900 dark:text-gray-100">
                    {t('settings.gitlab')}
                </h2>
                <div className="mt-5">
                    <div className="mt-2 flex space-between">
                        <label htmlFor="enable" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.enable_gitlab')}
                        </label>
                        <ToggleField name="gitlab.enabled" className="inline"/>
                    </div>
                </div>
                <div className="mt-5">
                    <LabelWithHelp htmlFor="user" help={t('settings.help.integration_user') || ''}>
                        {t('settings.gitlab_user')}
                    </LabelWithHelp>
                    <div className="mt-2">
                        <UserSelectField name="gitlab.user" placeholder={t('settings.gitlab_user_placeholder') || ''}/>
                    </div>
                </div>
                <div className="mt-5">
                    <label htmlFor="gitlab.url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.gitlab_url')}
                    </label>
                    <small>{t('settings.gitlab_url_desc')}</small>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="gitlab.url"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            placeholder="https://gitlab.example.com"
                        />
                    </div>
                </div>
                <div className="mt-5">
                    <label htmlFor="gitlab.token" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.gitlab_token')}
                    </label>
                    <small>{t('settings.gitlab_token_desc')}</small>
                    <div className="mt-2">
                        <SecretPasswordField
                            name="gitlab.token"
                            hasFlagName="gitlab.hasToken"
                            placeholder="glpat-…"
                        />
                    </div>
                </div>
                <h3 className="mt-8 mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">
                    {t('settings.gitlab_oauth')}
                </h3>
                <small>{t('settings.gitlab_oauth_desc')}</small>
                <div className="mt-5">
                    <label htmlFor="gitlab.oauth.client_id" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.gitlab_oauth_client_id')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="gitlab.oauth.client_id"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                    </div>
                </div>
                <div className="mt-5">
                    <label htmlFor="gitlab.oauth.client_secret" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.gitlab_oauth_client_secret')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="password"
                            name="gitlab.oauth.client_secret"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                    </div>
                </div>

                <h3 className="mt-8 mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">
                    {t('settings.gitlab_merge_requests')}
                </h3>
                <small>{t('settings.gitlab_merge_requests_desc')}</small>
                <div className="mt-5">
                    <div className="mt-2 flex space-between">
                        <label htmlFor="gitlab.merge_requests.squash" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.gitlab_mr_squash')}
                        </label>
                        <ToggleField name="gitlab.merge_requests.squash" className="inline"/>
                    </div>
                    <small>{t('settings.gitlab_mr_squash_desc')}</small>
                </div>
                <div className="mt-5">
                    <label htmlFor="gitlab.merge_requests.squash_commit_message" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.gitlab_mr_squash_commit_message')}
                    </label>
                    <small>{t('settings.gitlab_mr_squash_commit_message_desc')}</small>
                    <div className="mt-2">
                        <Field
                            as="select"
                            name="gitlab.merge_requests.squash_commit_message"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        >
                            <option value="first_commit">
                                {t('settings.gitlab_mr_squash_commit_first')}
                            </option>
                            <option value="gitlab_default">
                                {t('settings.gitlab_mr_squash_commit_gitlab_default')}
                            </option>
                        </Field>
                    </div>
                </div>
                <div className="mt-5">
                    <div className="mt-2 flex space-between">
                        <label htmlFor="gitlab.merge_requests.remove_source_branch" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.gitlab_mr_remove_source_branch')}
                        </label>
                        <ToggleField name="gitlab.merge_requests.remove_source_branch" className="inline"/>
                    </div>
                    <small>{t('settings.gitlab_mr_remove_source_branch_desc')}</small>
                </div>
            </div>
        </div>
    );
}

export default GitlabForm;
